import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ResizableSidebar } from '../ResizableSidebar/ResizableSidebar';
import { useShellLayout } from '../../provider';
import type { LayoutMode } from '../../state/shell-state';
import { surface } from '../Motion';

const LAYOUT_STORAGE_KEY = 'moo.v2.layoutMode';
const FLOAT_POS_KEY = 'moo.v2.floatPos';
const FLOAT_SIZE_KEY = 'moo.v2.floatSize';
const PANEL_Z = 1100;
/** z-index for popovers/tooltips that portal to `document.body` from inside
 *  the panel. Mantine's default popover z-index is 300, which sits BELOW the
 *  floating/fullscreen panel (`PANEL_Z`), so a portalled overlay with the
 *  default z renders behind the panel and is invisible in those layouts. */
export const PANEL_OVERLAY_Z = 1300;
const FLOAT_DEFAULT_W = 420;
const FLOAT_DEFAULT_H = 780; // 600 * 1.3 — 30% taller default floating window

function readStoredMode(): LayoutMode | null {
  try {
    const raw = globalThis.localStorage?.getItem(LAYOUT_STORAGE_KEY);
    if (raw === 'sidebar' || raw === 'fullscreen' || raw === 'floating')
      return raw;
  } catch {
    /* localStorage unavailable */
  }
  return null;
}

function persistMode(mode: LayoutMode): void {
  try {
    globalThis.localStorage?.setItem(LAYOUT_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

function readStoredPos(): { x: number; y: number } | null {
  try {
    const raw = globalThis.localStorage?.getItem(FLOAT_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;
    if (
      p &&
      typeof (p as { x: unknown }).x === 'number' &&
      typeof (p as { y: unknown }).y === 'number'
    ) {
      return p as { x: number; y: number };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function persistPos(pos: { x: number; y: number }): void {
  try {
    globalThis.localStorage?.setItem(FLOAT_POS_KEY, JSON.stringify(pos));
  } catch {
    /* ignore */
  }
}

function readStoredSize(): { w: number; h: number } | null {
  try {
    const raw = globalThis.localStorage?.getItem(FLOAT_SIZE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as unknown;
    if (
      s &&
      typeof (s as { w: unknown }).w === 'number' &&
      typeof (s as { h: unknown }).h === 'number'
    ) {
      return s as { w: number; h: number };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function persistSize(size: { w: number; h: number }): void {
  try {
    globalThis.localStorage?.setItem(FLOAT_SIZE_KEY, JSON.stringify(size));
  } catch {
    /* ignore */
  }
}

function windowWidth(): number {
  const w =
    typeof globalThis.innerWidth === 'number' ? globalThis.innerWidth : 0;
  return w > 0 ? w : 1920;
}

function windowHeight(): number {
  const h =
    typeof globalThis.innerHeight === 'number' ? globalThis.innerHeight : 0;
  return h > 0 ? h : 1080;
}

function defaultPos(): { x: number; y: number } {
  return {
    x: windowWidth() - FLOAT_DEFAULT_W - 24,
    y: windowHeight() - FLOAT_DEFAULT_H - 80,
  };
}

function clampPos(pos: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(pos.x, windowWidth() - 300)),
    y: Math.max(0, Math.min(pos.y, windowHeight() - 100)),
  };
}

export interface LayoutContainerProps {
  readonly isOpen: boolean;
  readonly children: ReactNode;
  /** Forwarded to ResizableSidebar. Persists sidebar width across reloads. */
  readonly sidebarStorageKey?: string;
}

export function LayoutContainer({
  isOpen,
  children,
  sidebarStorageKey,
}: LayoutContainerProps) {
  const { mode, setMode } = useShellLayout();

  // The sidebar slides out on close, so it stays rendered for one exit after
  // `isOpen` goes false. Derived during render, not in an effect: an effect
  // would first commit the closed (null) tree, unmounting the chat, and only
  // then bring it back to animate.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    setIsClosing(!isOpen && mode === 'sidebar');
  }
  const handleClosed = useCallback(() => setIsClosing(false), []);
  const [floatPos, setFloatPos] = useState<{ x: number; y: number }>(() =>
    clampPos(readStoredPos() ?? defaultPos())
  );
  const [floatSize, setFloatSize] = useState<{ w: number; h: number }>(
    () => readStoredSize() ?? { w: FLOAT_DEFAULT_W, h: FLOAT_DEFAULT_H }
  );
  const floatPanelRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const hydrated = useRef(false);

  // One-time hydration from localStorage — runs before the persist effect on
  // the same commit, but setMode schedules a re-render. The persist effect on
  // that first render would overwrite the stored value with the default
  // 'sidebar' before the re-render applies it; the hydrated flag prevents that.
  useEffect(() => {
    const stored = readStoredMode();
    if (stored && stored !== mode) setMode(stored);
    hydrated.current = true;
    // Intentionally empty deps — run once on mount (the moo lib's ESLint
    // config has no react-hooks plugin, so no disable directive is needed;
    // one would error as an unknown rule).
  }, []);

  // Persist on every mode change, but skip the initial render so we don't
  // overwrite a stored non-sidebar value before hydration has applied it.
  useEffect(() => {
    if (!hydrated.current) return;
    persistMode(mode);
  }, [mode]);

  // Track user resizes of the floating panel via ResizeObserver so the size
  // survives reloads. Only active in floating mode — the observer is torn down
  // when the mode switches so sidebar/fullscreen renders ignore it. isOpen is
  // also a dependency: the floating <aside> unmounts when the panel is
  // closed, so floatPanelRef is null until it reopens, and this effect must
  // re-run then to attach the observer to the newly mounted element.
  useEffect(() => {
    if (mode !== 'floating' || !isOpen) return undefined;
    const el = floatPanelRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      const w = Math.round(el.offsetWidth);
      const h = Math.round(el.offsetHeight);
      setFloatSize((prev) => {
        if (prev.w === w && prev.h === h) return prev;
        persistSize({ w, h });
        return { w, h };
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mode, isOpen]);

  const handleDragStart = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!(e.target as HTMLElement).closest('[data-drag-handle]')) return;
      if (
        (e.target as HTMLElement).closest(
          'button, [role="button"], a, input, textarea, select, [data-no-drag]'
        )
      )
        return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: floatPos.x,
        originY: floatPos.y,
      };
    },
    [floatPos]
  );

  const handleDragMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setFloatPos(
      clampPos({
        x: dragRef.current.originX + dx,
        y: dragRef.current.originY + dy,
      })
    );
  }, []);

  const handleDragEnd = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;
    const { startX, startY, originX, originY } = dragRef.current;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    // clientX/Y can be missing on some pointerup events (e.g. jsdom, or a
    // browser-cancelled gesture). Treat that as a 0 delta (no-op) rather
    // than falling back to `0 - startX`, which would jump the panel toward
    // the origin and persist a wrong floatPos.
    const dx =
      Number.isFinite(e.clientX) && Number.isFinite(startX)
        ? e.clientX - startX
        : 0;
    const dy =
      Number.isFinite(e.clientY) && Number.isFinite(startY)
        ? e.clientY - startY
        : 0;
    const finalPos = clampPos({ x: originX + dx, y: originY + dy });
    setFloatPos(finalPos);
    persistPos(finalPos);
  }, []);

  // The browser can cancel pointer capture mid-drag (e.g. touch interrupted
  // by a system gesture) without ever firing pointerup. Without this, dragRef
  // stays set and a later, unrelated pointermove would reposition the panel
  // using stale start coordinates.
  const handleDragCancel = useCallback(() => {
    dragRef.current = null;
  }, []);

  // ── OMNI-608: keep the chat subtree mounted across a layout switch ──
  // Each mode below renders a structurally different container
  // (ResizableSidebar vs <aside> vs <aside><div>). React cannot reconcile
  // different root element types, so changing mode would unmount and remount
  // `children` — and the V2 chat that lives there (ChatPanel owns `useChat`)
  // aborts its in-flight stream on unmount, dropping the running response and
  // resetting the view to the empty starter. To prevent that, `children` are
  // rendered ONCE into a single stable, detached DOM node via a portal; the
  // active mode's container merely re-adopts that node. Swapping the chrome no
  // longer touches the chat.
  const contentHostRef = useRef<HTMLDivElement | null>(null);
  if (contentHostRef.current === null && typeof document !== 'undefined') {
    const host = document.createElement('div');
    host.style.display = 'flex';
    host.style.flexDirection = 'column';
    host.style.flex = '1';
    host.style.minHeight = '0';
    host.style.overflow = 'hidden';
    contentHostRef.current = host;
  }

  // Ref callback on each mode's slot. When a mode's container mounts (including
  // after a mode switch remounts the chrome), move the stable host into it.
  const adoptSlot = useCallback((slot: HTMLDivElement | null) => {
    const host = contentHostRef.current;
    if (slot && host && host.parentNode !== slot) {
      slot.appendChild(host);
    }
  }, []);

  if (!isOpen && !(isClosing && mode === 'sidebar')) return null;

  const contentSlot = (
    <div
      ref={adoptSlot}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    />
  );

  // The chat renders here, into the stable host — never remounts on mode
  // change. In floating mode the drag handlers live on this wrapper, which is a
  // React ancestor of `children`, so a drag started from the chat header's
  // [data-drag-handle] reaches them (portaled synthetic events bubble through
  // the React tree, not the DOM parent <aside>). The wrapper structure is
  // identical in every mode — only the handler props toggle — so switching
  // layout never remounts the chat.
  const contentPortal = contentHostRef.current
    ? createPortal(
        <div
          className={surface.surface}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
          onPointerDown={mode === 'floating' ? handleDragStart : undefined}
          onPointerMove={mode === 'floating' ? handleDragMove : undefined}
          onPointerUp={mode === 'floating' ? handleDragEnd : undefined}
          onPointerCancel={mode === 'floating' ? handleDragCancel : undefined}
        >
          {children}
        </div>,
        contentHostRef.current
      )
    : null;

  let chrome: ReactNode;
  if (mode === 'sidebar') {
    chrome = (
      <ResizableSidebar
        isOpen={isOpen}
        isClosing={isClosing}
        onClosed={handleClosed}
        storageKey={sidebarStorageKey}
      >
        {contentSlot}
      </ResizableSidebar>
    );
  } else if (mode === 'fullscreen') {
    chrome = (
      <aside
        aria-label="Moo chat"
        data-layout-mode="fullscreen"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: PANEL_Z,
          background: 'var(--mantine-color-body)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {contentSlot}
      </aside>
    );
  } else {
    chrome = (
      <aside
        ref={floatPanelRef}
        aria-label="Moo chat"
        data-layout-mode="floating"
        style={{
          position: 'fixed',
          left: floatPos.x,
          top: floatPos.y,
          width: floatSize.w,
          height: floatSize.h,
          minWidth: 320,
          minHeight: 400,
          resize: 'both',
          overflow: 'hidden',
          zIndex: PANEL_Z,
          background: 'var(--mantine-color-body)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {contentSlot}
      </aside>
    );
  }

  return (
    <>
      {chrome}
      {contentPortal}
    </>
  );
}
